import {Component, OnInit} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {CrudActions} from '@application/helper/grid-action-buttons-helper/crud-actions.interface';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {RejectReason} from '@domain/settings/reject-reason/reject-reason';
import {HttpRejectReasonsService} from '@infrastructure/http/reject-reason/http-reject-reasons.service';
import {
  AlertDialogResult,
  AssertionUtils,
  BackendError,
  BaseComponent,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogType,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  TranslateService
} from '@vdw/angular-component-library';
import {filter, takeUntil} from 'rxjs';
import {RejectReasonOverviewService} from './reject-reason-overview.service';

@Component({
  templateUrl: './reject-reason-overview-page.component.html'
})
export class RejectReasonOverviewPageComponent extends BaseComponent implements OnInit {
  public config: CrudOverviewDataConfig<RejectReason>;
  private readonly pageUrls = LastModifiedCardUtils.getPageUrls('rejectReason');
  private readonly REJECT_REASON_TRANSLATION_KEY = 'BMSCONFIG.REJECT_REASON.REJECT_REASON';

  public constructor(
    private readonly crudOverviewDataBuilderFactory: CrudOverviewDataBuilderFactoryService<RejectReason>,
    private readonly rejectReasons: HttpRejectReasonsService,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly rejectReasonOverviewService: RejectReasonOverviewService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly navigationHelperService: NavigationHelperPrototypeService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService
  ) {
    super();
  }

  public ngOnInit(): void {
    const colDefs = this.rejectReasonOverviewService.getColumnDefs().colDefs;

    this.config = this.crudOverviewDataBuilderFactory
      .getBuilder()
      .withDefaultButtons({
        crudOverviewButtonConfigs: this.gridActionButtonsHelper.getActionButtonsForGridSelection({
          requiredPermissionToEdit: RouteUtils.paths.settings.rejectReason.editRejectReason.requiredPermission,
          withEdit: (grid: GridButtonClickedEvent) => this.navigateToGivenRoute(grid, 'edit'),
          withDuplicate: (grid: GridButtonClickedEvent) => this.navigateToGivenRoute(grid, 'duplicate'),
          withDelete: (grid: GridButtonClickedEvent) => this.delete(grid),
          allowDeleteMultiple: false
        } as CrudActions)
      })
      .withGridOptions(colDefs, GridIdentifier.REJECT_REASON_OVERVIEW, null, (builder: GridOptionsBuilder) =>
        this.rejectReasonOverviewService.getDefaultGridOptionsBuilder(builder).withRowSelection(false, true, true).build()
      )
      .withRowData(this.rejectReasons.getAll())
      .withEntityName(this.REJECT_REASON_TRANSLATION_KEY)
      .withNewClicked(() => this.navigationHelper.navigateWithRoute({route: this.pageUrls.add}))
      .build();
  }

  private navigateToGivenRoute({gridApi, rowNode}: GridButtonClickedEvent, action: string): void {
    this.navigationHelperService.navigateWithRoute({
      route: this.pageUrls[action].replace(':id', AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows()[0].id : rowNode.id)
    });
  }

  private delete({gridApi, rowNode}: GridButtonClickedEvent): void {
    const data = AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows()[0] : rowNode.data;

    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: this.translate.instant(this.REJECT_REASON_TRANSLATION_KEY, {count: 1}).toLowerCase()}),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(
        filter((result: AlertDialogResult) => result === AlertDialogResult.CONFIRM),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe({
        next: () => {
          this.rejectReasons
            .delete(data.id)
            .pipe(takeUntil(this.unSubscribeOnViewDestroy))
            .subscribe(() => {
              const rowsToRemove = AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows() : [rowNode.data];
              gridApi.applyTransaction({remove: rowsToRemove});
            });
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError('GENERAL.ACTIONS.DELETE_OBJECT', errorMessage.message)
      });
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.REJECT_REASON_TRANSLATION_KEY, {count: 1})
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }
}
